import { useRdPolicyDocument } from '@/features/quotes/hooks/rd-equipamentos/use-rd-policy-document.hook';
import { downloadBlob } from '@/shared/utils/download-blob.util';
import {
  Alert,
  Button,
  Spinner,
  Text,
} from '@noorden-insurtech/alba-design-system';
import { AlertCircle, ArrowLeft, Download } from 'lucide-react';
import { useEffect, useState } from 'react';

type RdPolicyDocumentProps = {
  proposalId: string;
};

/**
 * Tela da apólice RD Equipamentos (ramo 0171) — rota isolada, aberta pelo
 * "Ver detalhes" da lista de Apólices.
 *
 * Exibe o PDF emitido pela FOLHETERIA (motor → BFF `policy-pdf`), o mesmo
 * documento que o "Baixar PDF Apólice" entrega. Até 2026-08-28 esta tela
 * renderizava uma réplica HTML da apólice montada no front, com layout,
 * cláusulas e cálculos próprios: o corretor via um documento na tela e o
 * segurado recebia outro, e qualquer mudança no modelo da Folheteria não
 * chegava aqui. Existe uma única apólice RD — a da Folheteria.
 *
 * O PDF é carregado como `Blob` (a rota do BFF exige o JWT, então não dá
 * para apontar o `iframe` direto para a URL) e exibido por um object URL,
 * revogado ao sair da tela.
 */
export function RdPolicyDocument({ proposalId }: RdPolicyDocumentProps) {
  const { data, isLoading, isError, error } = useRdPolicyDocument(proposalId);
  const [objectUrl, setObjectUrl] = useState<string | null>(null);
  const [hasPreviewError, setHasPreviewError] = useState(false);

  useEffect(() => {
    if (!data?.blob) {
      setObjectUrl(null);
      return;
    }

    const url = URL.createObjectURL(data.blob);
    setObjectUrl(url);
    setHasPreviewError(false);

    return () => {
      URL.revokeObjectURL(url);
    };
  }, [data?.blob]);

  const reason =
    error instanceof Error && error.message.trim()
      ? error.message.trim()
      : 'Tente novamente mais tarde ou volte para a etapa de emissão.';

  return (
    <div className='fixed inset-0 z-50 flex flex-col bg-white'>
      <div className='flex shrink-0 items-center justify-between gap-3 border-b border-alba-gray-200 bg-white px-6 py-3'>
        <Button
          variant='ghost'
          size='md'
          className='flex items-center gap-2'
          onClick={() => window.history.back()}
        >
          <ArrowLeft className='h-5 w-5' strokeWidth={1.6} />
          Voltar
        </Button>

        <div className='hidden min-w-0 flex-col items-center leading-tight sm:flex'>
          <Text className='text-sm font-bold text-alba-black-500'>
            Apólice · RD Equipamentos
          </Text>
          {data?.filename && (
            <Text className='truncate text-xs text-alba-gray-600'>
              {data.filename}
            </Text>
          )}
        </div>

        <Button
          variant='primary'
          size='md'
          className='flex items-center gap-2'
          disabled={!data}
          onClick={() => data && downloadBlob(data.blob, data.filename)}
        >
          <Download className='h-5 w-5' strokeWidth={1.6} />
          Baixar PDF
        </Button>
      </div>

      <div className='min-h-0 flex-1 bg-alba-gray-100'>
        {isLoading ? (
          <div className='flex h-full flex-col items-center justify-center gap-3'>
            <Spinner size='lg' />
            <Text className='text-sm text-alba-black-400'>
              Carregando apólice...
            </Text>
          </div>
        ) : isError ? (
          <div className='mx-auto max-w-2xl px-6 py-10'>
            <Alert.Root status='error'>
              <Alert.Icon />
              <Alert.Content>
                <Alert.Title>Não foi possível abrir a apólice</Alert.Title>
                <Alert.Description>{reason}</Alert.Description>
              </Alert.Content>
            </Alert.Root>
          </div>
        ) : hasPreviewError || !objectUrl ? (
          /*
            O navegador pode não conseguir renderizar o PDF embutido (visualizador
            desativado, extensão bloqueando). O documento já está na memória, então
            o download continua valendo — dizer isso é melhor que uma moldura vazia.
          */
          <div className='flex h-full flex-col items-center justify-center gap-3 px-6 text-center'>
            <AlertCircle className='h-8 w-8 text-alba-gray-500' />
            <Text className='text-sm text-alba-gray-600'>
              Não foi possível exibir a apólice nesta tela. Use o botão "Baixar
              PDF" para abri-la.
            </Text>
          </div>
        ) : (
          <iframe
            src={objectUrl}
            title='Apólice RD Equipamentos'
            className='h-full w-full border-0'
            onError={() => setHasPreviewError(true)}
          />
        )}
      </div>
    </div>
  );
}
